import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment, isPaymentsConfigured } from "@/lib/stripe";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "@/hooks/use-toast";

/**
 * App-wide banner shown when the user's subscription is past_due so they
 * can update billing without having to dig into Settings.
 */
export function PastDueBanner() {
  const { pastDue } = useSubscription();
  const [opening, setOpening] = useState(false);
  if (!pastDue) return null;

  const handleOpen = async () => {
    if (!isPaymentsConfigured()) {
      toast({ title: "Unavailable", description: "Billing portal is not configured.", variant: "destructive" });
      return;
    }
    // iOS Safari (and most mobile browsers) only allow `window.open` if it's
    // called synchronously inside a user-gesture handler. Opening after an
    // `await` is blocked. We open a blank tab now and rewrite its URL once
    // the portal URL resolves; if the open was blocked we fall back to a
    // same-tab redirect.
    const popup = window.open("about:blank", "_blank");
    setOpening(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: { environment: getStripeEnvironment(), returnUrl: `${window.location.origin}/settings` },
      });
      if (error || !data?.url) {
        if (popup && !popup.closed) popup.close();
        toast({ title: "Couldn't open billing portal", description: error?.message ?? "Try again later.", variant: "destructive" });
      } else {
        const url = data.url as string;
        if (popup && !popup.closed) {
          popup.location.href = url;
        } else {
          window.location.href = url;
        }
      }
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="w-full bg-orange-100 border-b border-orange-300 px-4 py-2 text-sm text-orange-900 flex items-center justify-center gap-3 flex-wrap">
      <span className="flex items-center gap-1.5 font-medium">
        <AlertTriangle className="h-4 w-4" />
        Your last payment failed. Please update your billing details to keep access.
      </span>
      <button
        type="button"
        onClick={handleOpen}
        disabled={opening}
        className="underline font-medium hover:no-underline disabled:opacity-60"
      >
        {opening ? "Opening…" : "Update payment"}
      </button>
    </div>
  );
}